import React from 'react';
import { observer } from 'mobx-react-lite';
import store from '../stores/NotesStore';
import { fmt } from '../helpers/helpers';
import { resolveEntryLabel } from '../helpers/metadataHelpers';
import './EntryPaymentSourceModal.css';

/**
 * Picks the instrument that settles one entry, plus the "só lembrete" flag.
 *
 * Deliberately separate from PaymentSourceModal ("Pago com"), which links an
 * expense to the income that funded it. This one answers "cash or which card",
 * and both answers take the entry out of the header totals.
 */
const EntryPaymentSourceModal = observer(({ entry, onClose }) => {
  const sources = store.paymentSources;
  const selectedId = entry.paymentSourceId || null;
  const isReminderOnly = entry.reminderOnly === true;
  const isCard = store.paymentSourceById(selectedId)?.kind === 'credit-card';

  const select = (id) => {
    store.setEntryPaymentSource(entry.id, id === selectedId ? null : id);
  };

  // A card already keeps the entry out of the totals, so the checkbox would say
  // nothing new. It stays visible while actually checked, though — hiding a
  // ticked control would strand an entry flagged before the card was chosen,
  // with no way back.
  const showReminderToggle = !isCard || isReminderOnly;

  const countedIn = store.countedIn(entry);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="entry-payment-source-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Forma de pagamento — {resolveEntryLabel(entry, store)}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          <div className="source-value-hint">{fmt(entry.value)}</div>

          <div className="eps-list">
            <button
              className={`eps-option ${selectedId === null ? 'selected' : ''}`}
              onClick={() => store.setEntryPaymentSource(entry.id, null)}
            >
              <i className="fa-solid fa-money-bill-wave"></i>
              <span className="eps-option-name">Nenhuma (conta o valor)</span>
            </button>

            {sources.map(source => (
              <button
                key={source.id}
                className={`eps-option ${selectedId === source.id ? 'selected' : ''}`}
                onClick={() => select(source.id)}
              >
                <i className={source.kind === 'credit-card' ? 'fa-solid fa-credit-card' : 'fa-solid fa-wallet'}></i>
                <span className="eps-option-name">{source.name}</span>
                {source.kind === 'credit-card' && (
                  <span className="eps-option-tag">não entra no total</span>
                )}
              </button>
            ))}
          </div>

          {showReminderToggle && (
            <label className="eps-reminder">
              <input
                type="checkbox"
                checked={isReminderOnly}
                onChange={(e) => store.setEntryReminderOnly(entry.id, e.target.checked)}
              />
              <span>
                <strong>Só lembrete</strong>
                <small>Aparece na lista com vencimento, mas não entra em nenhum total.</small>
              </span>
            </label>
          )}

          <div className="eps-readout">
            {countedIn === 'viaCard' && 'Fora dos totais — a fatura do cartão já é uma linha separada.'}
            {countedIn === 'reminderOnly' && 'Fora dos totais — marcada como lembrete.'}
            {countedIn === 'cash' && 'Contando normalmente nos totais.'}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
});

export default EntryPaymentSourceModal;
